// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports

import { CustomAppState } from "@adl/App/Store/Reducers";
import { xGetFulfillmentLabel, xScreen } from "@adl/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: MiniChangeShipToStyles;
    onChangeShipToClick?: () => void;
}

const mapStateToProps = (state: CustomAppState) => {
    const { shipTo, fulfillmentMethod, pickUpWarehouse } = xGetFulfillmentLabel(state);
    return {
        fulfillmentMethod,
        shipTo,
        pickUpWarehouse,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface MiniChangeShipToStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    address?: AddressInfoDisplayStyles;
    changeShipToBtnWrapper?: InjectableCss;
    changeShipToBtn?: ButtonPresentationProps;
}

const shipToAddressText: TypographyPresentationProps = {
    size: 15,
    weight: "normal",
    css: css`
        letter-spacing: 0;
        line-height: 26px;
    `,
};

const miniChangeShipToStyles: MiniChangeShipToStyles = {
    wrapper: {
        css: css`
            min-width: 300px;
            ${xScreen("lte", "md")} {
                min-width: 250px;
            }
        `,
    },
    title: {
        variant: "h5",
        css: css`
            margin-bottom: 22px;
        `,
    },
    address: {
        address1Text: shipToAddressText,
        cityText: shipToAddressText,
        postalCodeText: shipToAddressText,
        stateText: shipToAddressText,
    },
    changeShipToBtnWrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: center;
            margin-top: 18px;
        `,
    },
    changeShipToBtn: {
        variant: "secondary",
    },
};

const MiniChangeShipTo = ({
    fulfillmentMethod,
    shipTo,
    onChangeShipToClick,
    pickUpWarehouse,
    extendedStyles,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(miniChangeShipToStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography variant="h5">
                {translate(
                    `My Default ${fulfillmentMethod === FulfillmentMethod.Ship ? "Shipping" : "Pickup"} Address`,
                )}
            </Typography>
            {fulfillmentMethod === FulfillmentMethod.Ship && (
                <AddressInfoDisplay
                    address1={shipTo?.address1 || ""}
                    city={shipTo?.city || ""}
                    state={shipTo?.state?.abbreviation || ""}
                    postalCode={shipTo?.postalCode || ""}
                    extendedStyles={styles.address}
                />
            )}
            {fulfillmentMethod === FulfillmentMethod.PickUp && (
                <AddressInfoDisplay
                    address1={pickUpWarehouse?.address1 || ""}
                    city={pickUpWarehouse?.city || ""}
                    state={pickUpWarehouse?.state || ""}
                    postalCode={pickUpWarehouse?.postalCode || ""}
                    extendedStyles={styles.address}
                />
            )}
            {/* XNG-Changes hide change button for Pickup fulfillment method */}
            {fulfillmentMethod === FulfillmentMethod.Ship && (
                <StyledWrapper {...styles.changeShipToBtnWrapper}>
                    <Button {...styles.changeShipToBtn} onClick={onChangeShipToClick}>
                        {translate(`Change Ship-To`)}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(MiniChangeShipTo);
